import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, CreditCard, MapPin, PackagePlus, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

export const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const [shipments, setShipments] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.listShipments({}, 10, 0),
      apiClient.listPayments({}, 10, 0),
    ]).then(([s, p]) => {
      const sd = s.data.data; setShipments(sd?.shipments ?? sd?.rows ?? (Array.isArray(sd) ? sd : []));
      const pd = p.data.data; setPayments(pd?.payments ?? pd?.rows ?? (Array.isArray(pd) ? pd : []));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const inTransit = shipments.filter(s => s.status === 'IN_TRANSIT').length;
  const delivered = shipments.filter(s => s.status === 'DELIVERED').length;
  const pendingPayments = payments.filter(p => p.status === 'PENDING').length;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Welcome, {user?.firstName}!</h1>
        <Link to="/user/shipments/create" className="btn btn-primary"><PackagePlus size={16} /> Send Package</Link>
      </div>

      {loading ? <div className="loading-spinner">Loading...</div> : (
        <>
          <div className="stats-grid">
            {[
              { label: 'My Shipments', value: shipments.length, color: '#3b82f6', icon: <Package size={20} />, link: '/user/shipments' },
              { label: 'In Transit', value: inTransit, color: '#f59e0b', icon: <Clock size={20} />, link: '/user/shipments?status=IN_TRANSIT' },
              { label: 'Delivered', value: delivered, color: '#22c55e', icon: <CheckCircle size={20} />, link: '/user/shipments?status=DELIVERED' },
              { label: 'Pending Payments', value: pendingPayments, color: '#ef4444', icon: <CreditCard size={20} />, link: '/user/payments' },
            ].map(s => (
              <Link to={s.link} key={s.label} style={{ textDecoration: 'none' }}>
                <div className="stat-card" style={{ borderLeft: `4px solid ${s.color}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div className="stat-label">{s.label}</div>
                      <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                    </div>
                    <div style={{ color: s.color, opacity: 0.6 }}>{s.icon}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="card">
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>My Shipments</h2>
                <Link to="/user/shipments" className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Tracking #</th><th>Receiver</th><th>Status</th></tr></thead>
                  <tbody>
                    {shipments.slice(0, 6).map((s: any) => (
                      <tr key={s.id}>
                        <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                        <td>{s.receiverName}</td>
                        <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>My Payments</h2>
                <Link to="/user/payments" className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Shipment</th><th>Amount</th><th>Status</th></tr></thead>
                  <tbody>
                    {payments.slice(0, 6).map((p: any) => (
                      <tr key={p.id}>
                        <td><code style={{ fontSize: 11 }}>{p.shipment?.trackingNumber ?? p.shipmentId}</code></td>
                        <td>{Number(p.amount).toLocaleString()} RWF</td>
                        <td><span className={`badge badge-${p.status?.toLowerCase()}`}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 20 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Quick Actions</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'Send Package', to: '/user/shipments/create', icon: <PackagePlus size={15} /> },
                { label: 'Track Shipment', to: '/user/tracking', icon: <MapPin size={15} /> },
                { label: 'My Payments', to: '/user/payments', icon: <CreditCard size={15} /> },
              ].map(a => (
                <Link key={a.label} to={a.to} className="btn btn-ghost">{a.icon} {a.label}</Link>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
